/**
 * Zero-sum scoring domain. SPEC.md §2, §3, §11, §25.
 *
 * Every rule here is pure and shared: the server enforces it, the client uses
 * it to render labels and disabled states. Neither side may invent its own.
 *
 * The whole of the scoring model is one sentence: **players report only their
 * own losses, and the winner is whoever reported nothing.** Nobody declares a
 * winner and nobody picks a mode, because both are already implied by what the
 * table typed. A hand is therefore not a thing with a shape — it is a set of
 * reported losses, and the transfers are *derived* from that set every time it
 * changes. §3
 *
 * That derivation is `resolveHand`, and it is the only place a loss becomes a
 * from/to pair. `diffHandTransfers` is what lets the ledger follow it without
 * being rewritten wholesale: rows that still belong are left exactly as they
 * were, and rows that no longer do are cancelled with an inverse rather than
 * deleted. §12.2
 */

import {
  MAX_POINTS_PER_DRINK,
  MAX_TRANSFER_AMOUNT,
  MIN_POINTS_PER_DRINK,
  MIN_TRANSFER_AMOUNT,
} from './constants.js';
import { DomainError } from './errors.js';
import type {
  Adjustment,
  AdjustmentKind,
  EntryShape,
  HandEntry,
  Round,
  Transfer,
} from './types.js';

// --- Entries -----------------------------------------------------------------

/** The entry a player currently has standing for a hand, if any. §12.2 */
export function entryOf(
  entries: readonly HandEntry[],
  playerId: string | null,
): HandEntry | null {
  if (!playerId) return null;
  return entries.find((entry) => entry.playerId === playerId) ?? null;
}

/**
 * Whether this player may report a loss right now. §11
 *
 * Everyone at the table may — there is no focal player to exclude any more, and
 * a player who has nothing to report simply does not report, which is precisely
 * how they come to be the winner. All this gates is a live hand they are part of.
 */
export function canPlayerEnterHand(params: {
  participantIds: readonly string[];
  playerId: string | null;
  roundActive: boolean;
}): boolean {
  if (!params.roundActive || !params.playerId) return false;
  return params.participantIds.includes(params.playerId);
}

/**
 * What reporting this loss does to the reporting player's own score. §2.1
 *
 * TO_EVERYONE pays the full amount to every other participant, so 20 at a table
 * of five is 80 out — the number they typed is not the number they lose, which
 * is exactly why this is shown before they commit.
 */
export function handEntryImpact(input: {
  shape: EntryShape;
  amount: number;
  participantCount: number;
}): number {
  const width =
    input.shape === 'TO_EVERYONE' ? Math.max(1, input.participantCount - 1) : 1;
  return -(input.amount * width);
}

// --- Resolution --------------------------------------------------------------

/**
 * What every derivation of a hand is asked about. §3, §3.3, §31
 *
 * Two lists, and the difference between them is the whole of what removing a
 * player mid-hand means: `participantIds` is who the hand was dealt to and never
 * changes, `departedPlayerIds` is who has since left the table. The first
 * decides who is *paid*, the second who is *waited for*.
 */
export interface HandDerivation {
  entries: readonly HandEntry[];
  /** Frozen when the hand opened. §3.3 */
  participantIds: readonly string[];
  /** Participants the host has since removed. Empty for almost every hand. §31 */
  departedPlayerIds?: readonly string[];
  /**
   * Which kind of table this hand is being played at. §4.6
   *
   * **Required, and deliberately not defaulted.** It changes what a loss to
   * everyone *does* — at an ordinary table the reporter pays each opponent, and
   * at a drinking table the same points are destroyed and nobody is paid — so a
   * derivation that did not have to say which kind of table it was would quietly
   * pick one. That is exactly the bug this field was added to fix: drinking mode
   * had no answer for the shape at all, so every "pay everybody" hand was scored
   * as if the table were ordinary and the points came back to the other players.
   *
   * `departedPlayerIds` above is optional because absent means empty, and empty
   * is both the common case and the harmless one. There is no harmless default
   * here: whichever way it fell, one of the two modes would be wrong.
   */
  drinkingMode: boolean;
}

/** A transfer a hand implies, before it is given an id or a sequence. */
export interface PlannedTransfer {
  fromPlayerId: string;
  toPlayerId: string;
  amount: number;
  /** The entry this row stands for, which is what makes a re-derivation stable. */
  handEntryId: string;
}

export interface ResolvedHand {
  /** Every transfer the hand's entries currently imply. Always sums to zero. §2.2 */
  rows: PlannedTransfer[];
  /** The player everyone's losses go to, or null while more than one is silent. */
  winnerPlayerId: string | null;
  /** Participants who have reported nothing. The winner is the last one left. */
  silentPlayerIds: string[];
  /** Entries that cannot be placed yet, because the winner is not inferable. */
  pendingEntryIds: string[];
  /**
   * True once every reported loss has been accounted for.
   *
   * Not "has become transfers", which it used to be and no longer is: a loss to
   * everyone at a drinking table becomes an *adjustment* and no transfers at all,
   * and calling that pending would leave `handOutcome` returning null forever —
   * the hand would sit open with nothing left that could close it. §4.6, §4.3
   */
  settled: boolean;
}

/**
 * The one player who reported nothing, or null when that is not yet a single
 * person. §3.1
 *
 * Silence is the whole signal. Two silent players means the hand has not
 * finished being reported; none means every player claims to have lost, which
 * nobody can win — and both are states the UI has to be able to say out loud
 * rather than states this function is allowed to guess its way out of.
 */
export function inferWinner(
  entries: readonly HandEntry[],
  participantIds: readonly string[],
  departedPlayerIds: readonly string[] = [],
): string | null {
  const silent = silentPlayers(entries, participantIds, departedPlayerIds);
  return silent.length === 1 ? (silent[0] ?? null) : null;
}

/**
 * Participants the hand is still waiting on. §3.1, §31
 *
 * Silence is the signal, so it has to mean "has not reported *yet*" — and a
 * player the host has taken off the table is never going to. Left in, they are
 * silent forever: a hand of four with one removed can never get down to a single
 * silent player, so the winner is never inferable, the losses stay pending and
 * the hand cannot end. Every remaining player reports, watches nothing happen,
 * and has no way to say the missing one is not coming.
 *
 * `departedPlayerIds` is therefore not the same question as `participantIds`,
 * and both are needed: the hand still *pays* everybody it was dealt to — a loss
 * to everyone that already reached someone is not taken back off them because
 * they later left (§31) — it just stops expecting anything more from them.
 */
export function silentPlayers(
  entries: readonly HandEntry[],
  participantIds: readonly string[],
  departedPlayerIds: readonly string[] = [],
): string[] {
  const reported = new Set(entries.map((entry) => entry.playerId));
  const departed = new Set(departedPlayerIds);
  return participantIds.filter((id) => !reported.has(id) && !departed.has(id));
}

/**
 * Expands a hand's reported losses into the transfers they mean. §2.1, §3
 *
 * TO_EVERYONE needs nothing but the participants: one row per opponent, each for
 * the full amount. Nothing is divided, so there is no remainder to round.
 *
 * TO_WINNER needs the winner, so those rows appear only once exactly one player
 * is silent. Until then they are pending — reported, visible, and deliberately
 * not yet part of anyone's score, because paying them to a guess would be worse
 * than making the table wait for the last report. §3.1
 *
 * Every row moves points from one player to another, so the result is zero-sum
 * by construction rather than by arithmetic anyone has to check. §2.2
 */
export function resolveHand(input: HandDerivation): ResolvedHand {
  const { entries, participantIds, departedPlayerIds } = input;
  const participants = participantIds.filter(
    (id, index) => participantIds.indexOf(id) === index,
  );

  const rows: PlannedTransfer[] = [];
  const pendingEntryIds: string[] = [];

  /**
   * Who is still to report is asked of the players who *can*, but who gets paid
   * is asked of everybody the hand was dealt to. A player removed mid-hand stops
   * being waited for and keeps what the hand already owed them. §3.3, §31
   */
  const silentPlayerIds = silentPlayers(entries, participants, departedPlayerIds);
  const winnerPlayerId = silentPlayerIds.length === 1 ? (silentPlayerIds[0] ?? null) : null;

  for (const entry of entries) {
    // An entry from someone who is not part of this hand cannot be placed: they
    // have no counterparties in it. §3.3
    if (!participants.includes(entry.playerId)) {
      pendingEntryIds.push(entry.id);
      continue;
    }

    if (entry.shape === 'TO_EVERYONE') {
      const opponents = participants.filter((id) => id !== entry.playerId);
      if (opponents.length === 0) {
        pendingEntryIds.push(entry.id);
        continue;
      }
      /*
        At a drinking table this report moves nothing *between* players: the
        reporter pays for the whole table and those points leave the game rather
        than reaching anybody. So there are no transfers to write, and the cost
        lands as the one-ended `everyone-loss` in `everyoneLosses` below. §4.6

        Placed, not pending — the entry has been fully accounted for, just not by
        a transfer. Pending would mean the hand can never close (see `settled`).
      */
      if (input.drinkingMode) continue;
      for (const opponentId of opponents) {
        rows.push({
          fromPlayerId: entry.playerId,
          toPlayerId: opponentId,
          amount: entry.amount,
          handEntryId: entry.id,
        });
      }
      continue;
    }

    if (winnerPlayerId === null) {
      pendingEntryIds.push(entry.id);
      continue;
    }
    rows.push({
      fromPlayerId: entry.playerId,
      toPlayerId: winnerPlayerId,
      amount: entry.amount,
      handEntryId: entry.id,
    });
  }

  return {
    rows,
    winnerPlayerId,
    silentPlayerIds,
    pendingEntryIds,
    settled: pendingEntryIds.length === 0,
  };
}

/**
 * Why a hand is over. §4.3
 *
 * `winner-inferred` — every loser has said so, which leaves exactly one player
 * silent, and the hand is theirs. `paid-everyone` — one player reported paying
 * the whole table, which needs no winner and leaves nobody else with a loss to
 * report.
 */
export type HandOutcome = 'winner-inferred' | 'paid-everyone';

/**
 * Whether the hand has arrived somewhere no further report is expected to take
 * it, and why — or null while the table still owes it one. §4.3
 *
 * This is the whole of "a hand closes itself". Nobody taps Next hand, because by
 * the time there would be something to tap the answer is already on the table:
 * either one player is the last one silent, or somebody has paid everybody. Both
 * are states a card table recognises as the end of a hand without being told.
 *
 * It is deliberately narrow. Every reported loss must already have become
 * transfers — a report that could not be placed means the hand is still being
 * told, whatever else is true of it — and the two shapes below are the only ones
 * that end it. A hand nobody has reported into, a hand with two players still
 * silent, a hand where everybody claims to have lost: all still being played.
 */
export function handOutcome(input: HandDerivation): HandOutcome | null {
  const { entries } = input;
  if (entries.length === 0) return null;

  const resolved = resolveHand(input);
  if (!resolved.settled) return null;

  if (resolved.silentPlayerIds.length === 1) return 'winner-inferred';

  /**
   * One player paying the whole table is a complete hand on its own: everybody
   * else received, so nobody else has anything left to report. A second report
   * standing means the table is playing an ordinary hand after all, and the rule
   * above is the one that ends it. §3.2
   */
  const only = entries.length === 1 ? entries[0] : undefined;
  return only?.shape === 'TO_EVERYONE' ? 'paid-everyone' : null;
}

// --- Materialisation ---------------------------------------------------------

/**
 * The transfers of a round that currently count: neither an inverse, nor
 * cancelled by one. §12.2
 *
 * History shows both halves of a correction, so "what the ledger says now" has
 * to be a derivation rather than the row list itself.
 */
export function liveTransfers(transfers: readonly Transfer[]): Transfer[] {
  const reversedIds = new Set(
    transfers.map((t) => t.reversesTransferId).filter((id): id is string => Boolean(id)),
  );
  return transfers.filter((t) => !t.reversesTransferId && !reversedIds.has(t.id));
}

export interface TransferDiff {
  /** Live rows the hand no longer implies. Cancelled with an inverse each. §12.2 */
  reverse: Transfer[];
  /** Rows the hand implies that are not on the ledger yet. */
  insert: PlannedTransfer[];
}

function rowKey(row: {
  handEntryId?: string | null;
  fromPlayerId: string;
  toPlayerId: string;
  amount: number;
}): string {
  return [
    row.handEntryId ?? '',
    row.fromPlayerId,
    row.toPlayerId,
    String(row.amount),
  ].join('|');
}

/**
 * What has to change for the ledger to say what the hand now says. §12.2
 *
 * The point of the diff is everything it leaves alone. Re-deriving a hand from
 * scratch on every keystroke would be correct and unreadable — four inverses and
 * four fresh rows every time one player fixed a typo. Keyed on the entry a row
 * came from, an amended entry disturbs only its own rows, and the rest of the
 * table's history stays where it was.
 *
 * `roundTransfers` is every row of the round, corrections included; which of
 * them still count is this function's business and not the caller's.
 */
export function diffHandTransfers(
  roundTransfers: readonly Transfer[],
  desired: readonly PlannedTransfer[],
): TransferDiff {
  const live = liveTransfers(roundTransfers);
  const liveKeys = new Set(live.map(rowKey));
  const desiredKeys = new Set(desired.map(rowKey));

  return {
    reverse: live.filter((row) => !desiredKeys.has(rowKey(row))),
    insert: desired.filter((row) => !liveKeys.has(rowKey(row))),
  };
}

// --- Drinking mode -----------------------------------------------------------
//
// The one variant where the points are not expected to balance, and everything
// that makes that true lives here. §4.6
//
// There are two hands a table can play, and drinking mode changes both of them.
//
// **A hand with a winner: the winner takes only the largest loss reported into
// it, not the sum.** Three players, A silent, B reports 7 and C reports 14, and A
// ends the hand +14 rather than +21. This is expressed as a correction to the
// ordinary derivation rather than as a second one: `resolveHand` still pays every
// loss to the winner exactly as at a normal table, and `winnerSpill` subtracts
// what they do not keep. The ledger of who paid what to whom stays the ledger
// every other rule already reads. §60
//
// **A hand where one player pays everybody: the points are destroyed, and nobody
// is paid.** They lose the amount times the opponents — the same figure the keypad
// quoted them — and the other players get nothing, where an ordinary table would
// have handed each of them the full amount. This one cannot be a correction on
// top of the ordinary derivation, because there is nothing left of it to correct:
// every transfer it would write is a transfer that must not exist. So
// `resolveHand` takes the mode and writes none of them, and `everyoneLosses`
// carries the whole cost as one row against one player.
//
// That asymmetry is the honest shape of it. The winner's rule adjusts a real
// movement of points; this one denies that any movement happened at all. Writing
// N transfers and then N adjustments to take them back would leave a history that
// claims "D paid A 10" at a table where A received nothing.

/** An adjustment a hand implies, before it is given an id or a sequence. */
export interface PlannedAdjustment {
  playerId: string;
  /** Signed, never zero. */
  amount: number;
  kind: AdjustmentKind;
}

/**
 * Kinds that are *derived* from the hand, and are therefore the diff's business.
 *
 * A drink is not one of them: a player pressed a button and said they finished
 * it, which is an event rather than a consequence. Re-deriving the hand must
 * leave it exactly where it is, or a report landing a second later would cancel
 * somebody's drink. §4.6, §12.2
 */
export function isDerivedAdjustment(kind: AdjustmentKind): boolean {
  return kind === 'winner-spill' || kind === 'everyone-loss';
}

/**
 * What a loss to everyone costs at a drinking table. §4.6
 *
 * One row per such report, against the reporter, for the whole of it: the amount
 * they typed times the opponents it was quoted against. Nobody is credited,
 * because at a drinking table this shape destroys the points rather than
 * distributing them — which is the half of drinking mode that was missing, and
 * why a "pay everybody" hand used to score identically to an ordinary table's.
 *
 * The multiplier is `participants - 1`, the same width `handEntryImpact` prices
 * the report at before the player commits, so the number the keypad warned them
 * about is the number that lands. Participants are the list frozen when the hand
 * opened, so a player removed mid-hand does not change what an already-standing
 * report costs. §3.3, §31
 *
 * Empty at an ordinary table, where the transfers in `resolveHand` are the whole
 * story and the table still balances.
 */
export function everyoneLosses(input: HandDerivation): PlannedAdjustment[] {
  if (!input.drinkingMode) return [];
  const { entries, participantIds } = input;
  const participants = participantIds.filter(
    (id, index) => participantIds.indexOf(id) === index,
  );

  const rows: PlannedAdjustment[] = [];
  for (const entry of entries) {
    if (entry.shape !== 'TO_EVERYONE') continue;
    if (!participants.includes(entry.playerId)) continue;
    const opponents = participants.length - 1;
    if (opponents <= 0) continue;
    rows.push({
      playerId: entry.playerId,
      amount: -(entry.amount * opponents),
      kind: 'everyone-loss',
    });
  }
  return rows;
}

/**
 * Whether this report would contradict what the hand already says. §3.2
 *
 * A hand is one of two stories and cannot be both: either a single player paid
 * everybody — which is the whole hand, and leaves nobody else with a loss to
 * report — or every loser reports their own loss and the silent one wins. A hand
 * holding both says one player owes the table *and* that somebody else lost to a
 * winner, which is not a state a card table can be in.
 *
 * The check is against the hand as it *would be*, so the reporter's own standing
 * entry is excluded: a report replaces what that player said before (§12.2), so
 * the only player who may switch a hand between the two shapes is the one who is
 * alone in it.
 *
 * Both directions, and the second is only reachable in data that predates the
 * rule: a loss to everyone standing alone closes its hand immediately, so nothing
 * can be added to it afterwards. Checking it anyway costs one predicate and means
 * a hand recovered from before this rule cannot be made worse.
 */
export function conflictsWithHand(input: {
  shape: EntryShape;
  playerId: string;
  entries: readonly HandEntry[];
}): boolean {
  const others = input.entries.filter((entry) => entry.playerId !== input.playerId);
  if (others.length === 0) return false;
  if (input.shape === 'TO_EVERYONE') return true;
  return others.some((entry) => entry.shape === 'TO_EVERYONE');
}

/**
 * What the winner of a drinking hand does *not* take. §4.6
 *
 * Null when there is nothing to take back: no winner yet, no losses reported to
 * one, or a single loser — whose loss is both the sum and the largest, so the
 * ordinary derivation already paid exactly the right amount.
 *
 * Only losses reported TO_WINNER count. A player paying the whole table is
 * self-contained and needs no winner at all, so there is nothing about it for
 * this rule to be about. §3.2
 */
export function winnerSpill(input: HandDerivation): PlannedAdjustment | null {
  const { entries, participantIds, departedPlayerIds } = input;
  const winnerPlayerId = inferWinner(entries, participantIds, departedPlayerIds);
  if (winnerPlayerId === null) return null;

  const losses = entries
    .filter(
      (entry) => entry.shape === 'TO_WINNER' && participantIds.includes(entry.playerId),
    )
    .map((entry) => entry.amount);
  if (losses.length === 0) return null;

  const spill = losses.reduce((sum, amount) => sum + amount, 0) - Math.max(...losses);
  if (spill === 0) return null;

  return { playerId: winnerPlayerId, amount: -spill, kind: 'winner-spill' };
}

/**
 * Every adjustment a hand implies right now. §4.6
 *
 * Up to one `everyone-loss` per player who paid the whole table, plus at most one
 * `winner-spill`. In practice a settled hand is one or the other, because a
 * report of either shape refuses to join a hand holding the other
 * (`conflictsWithHand`) — but the derivation does not depend on that, so a hand
 * recorded before the rule still resolves to something coherent.
 *
 * An empty list at an ordinary table, and that is what keeps drinking mode from
 * leaking into it: the diff below then cancels anything a hand had, which is
 * exactly what a table needs if its mode were ever to change under it.
 */
export function resolveHandAdjustments(input: HandDerivation): PlannedAdjustment[] {
  if (!input.drinkingMode) return [];
  const spill = winnerSpill(input);
  return [...everyoneLosses(input), ...(spill ? [spill] : [])];
}

/** The adjustments that currently count: neither an inverse, nor cancelled. §12.2 */
export function liveAdjustments(adjustments: readonly Adjustment[]): Adjustment[] {
  const reversedIds = new Set(
    adjustments
      .map((a) => a.reversesAdjustmentId)
      .filter((id): id is string => Boolean(id)),
  );
  return adjustments.filter((a) => !a.reversesAdjustmentId && !reversedIds.has(a.id));
}

export interface AdjustmentDiff {
  /** Live derived rows the hand no longer implies. Cancelled with an inverse. §12.2 */
  reverse: Adjustment[];
  /** Rows the hand implies that are not on the ledger yet. */
  insert: PlannedAdjustment[];
}

function adjustmentKey(row: { playerId: string; amount: number; kind: AdjustmentKind }): string {
  return [row.kind, row.playerId, String(row.amount)].join('|');
}

/**
 * What has to change for the adjustments to say what the hand now says. §12.2
 *
 * The same shape as `diffHandTransfers`, and the same reason for it: a spill
 * that has not changed is left alone, so amending one player's report does not
 * churn a row that still says the truth. Entered kinds are invisible to it, so a
 * drink is never something a re-derivation can take away.
 */
export function diffHandAdjustments(
  roundAdjustments: readonly Adjustment[],
  desired: readonly PlannedAdjustment[],
): AdjustmentDiff {
  const live = liveAdjustments(roundAdjustments).filter((row) => isDerivedAdjustment(row.kind));
  const liveKeys = new Set(live.map(adjustmentKey));
  const desiredKeys = new Set(desired.map(adjustmentKey));

  return {
    reverse: live.filter((row) => !desiredKeys.has(adjustmentKey(row))),
    insert: desired.filter((row) => !liveKeys.has(adjustmentKey(row))),
  };
}

/** The exact inverse of an adjustment. A correction never deletes. §12.2 */
export function inverseAdjustmentOf(adjustment: Adjustment): {
  playerId: string;
  amount: number;
  kind: AdjustmentKind;
} {
  return {
    playerId: adjustment.playerId,
    amount: -adjustment.amount,
    kind: adjustment.kind,
  };
}

// --- Validation --------------------------------------------------------------

/** Validates a point amount. V1 is positive integers only. §10.3 */
export function assertValidAmount(amount: number): void {
  if (
    !Number.isInteger(amount) ||
    amount < MIN_TRANSFER_AMOUNT ||
    amount > MAX_TRANSFER_AMOUNT
  ) {
    throw new DomainError('INVALID_AMOUNT', `Rejected amount: ${String(amount)}`);
  }
}

/** A transfer must move points between two distinct players. §2.1 */
export function assertDistinctPlayers(fromPlayerId: string, toPlayerId: string): void {
  if (fromPlayerId === toPlayerId) {
    throw new DomainError('SELF_TRANSFER');
  }
}

/** What a cup is worth, fixed when the table is created. §4.6 */
export function assertValidPointsPerDrink(points: number): void {
  if (
    !Number.isInteger(points) ||
    points < MIN_POINTS_PER_DRINK ||
    points > MAX_POINTS_PER_DRINK
  ) {
    throw new DomainError('INVALID_AMOUNT', `Rejected points per drink: ${String(points)}`);
  }
}

// --- Totals ------------------------------------------------------------------

/**
 * Derives every player's score from transfers: incoming minus outgoing. §25
 *
 * `playerIds` seeds the map so players with no activity still report 0 rather
 * than being absent, which keeps the UI from rendering blanks.
 *
 * `adjustments` is drinking mode's half of the same sum and is empty everywhere
 * else, so a table that never turned it on derives exactly what it always did.
 * They are summed whole, inverses included, for the same reason transfers are:
 * an inverse is the exact opposite of what it cancels, so the pair contributes
 * nothing without anybody having to work out which rows still count. §12.2
 */
export function deriveTotals(
  transfers: readonly Transfer[],
  playerIds: readonly string[] = [],
  adjustments: readonly Adjustment[] = [],
): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const id of playerIds) totals[id] = 0;

  for (const t of transfers) {
    totals[t.fromPlayerId] = (totals[t.fromPlayerId] ?? 0) - t.amount;
    totals[t.toPlayerId] = (totals[t.toPlayerId] ?? 0) + t.amount;
  }
  for (const a of adjustments) {
    totals[a.playerId] = (totals[a.playerId] ?? 0) + a.amount;
  }
  return totals;
}

/** Same derivation, restricted to one round. §25 */
export function deriveRoundTotals(
  transfers: readonly Transfer[],
  roundId: string | null,
  playerIds: readonly string[] = [],
  adjustments: readonly Adjustment[] = [],
): Record<string, number> {
  if (!roundId) {
    const empty: Record<string, number> = {};
    for (const id of playerIds) empty[id] = 0;
    return empty;
  }
  return deriveTotals(
    transfers.filter((t) => t.roundId === roundId),
    playerIds,
    adjustments.filter((a) => a.roundId === roundId),
  );
}

export interface BalanceTotals {
  positiveTotal: number;
  negativeTotal: number;
  balanced: boolean;
}

/**
 * Splits a totals map into the received/paid figures shown at the table
 * centre. §14.1 `negativeTotal` is returned as a negative number so callers can
 * render it verbatim.
 */
export function balanceTotals(totals: Record<string, number>): BalanceTotals {
  let positiveTotal = 0;
  let negativeTotal = 0;
  for (const value of Object.values(totals)) {
    if (value > 0) positiveTotal += value;
    else negativeTotal += value;
  }
  return {
    positiveTotal,
    negativeTotal,
    balanced: positiveTotal + negativeTotal === 0,
  };
}

/** Sums a totals map. Must be exactly 0 for any valid session or round. §2.2 */
export function sumTotals(totals: Record<string, number>): number {
  let sum = 0;
  for (const value of Object.values(totals)) sum += value;
  return sum;
}

export function isZeroSum(totals: Record<string, number>): boolean {
  return sumTotals(totals) === 0;
}

/**
 * Integrity guard. A non-zero sum means the data model was bypassed, which is a
 * bug rather than a user error — callers log it and resync. §14.3
 */
export function assertZeroSum(totals: Record<string, number>, context: string): void {
  const sum = sumTotals(totals);
  if (sum !== 0) {
    throw new DomainError('INTERNAL', `Zero-sum invariant violated in ${context}: sum=${sum}`);
  }
}

// --- Transfer helpers --------------------------------------------------------

/** Who a transfer is logically attributed to. §30 */
export function attributedPlayerId(transfer: Transfer): string {
  return transfer.actedForPlayerId ?? transfer.createdByPlayerId;
}

/** Whether a transfer has been reversed by any other transfer in the set. §12.2 */
export function isReversed(transfer: Transfer, all: readonly Transfer[]): boolean {
  return all.some((t) => t.reversesTransferId === transfer.id);
}

/** The exact inverse of a transfer. A correction never deletes. §12.2 */
export function inverseOf(transfer: Transfer): {
  fromPlayerId: string;
  toPlayerId: string;
  amount: number;
} {
  return {
    fromPlayerId: transfer.toPlayerId,
    toPlayerId: transfer.fromPlayerId,
    amount: transfer.amount,
  };
}

// --- Copy --------------------------------------------------------------------
//
// The words a player reads and the rule they describe are the same thing, so
// they live together. A label built in a component is a rule restated. §60

/** The action-stack button for a shape. §11 */
export function entryActionLabel(shape: EntryShape, amount: number): string {
  return shape === 'TO_EVERYONE'
    ? `Lose ${String(amount)} to everyone`
    : `Lose ${String(amount)}`;
}

/**
 * The line under it, which is where the fan-out is spelled out. §2.1, §4.6
 *
 * Kept under about forty characters for the same reason the status details are:
 * it sits between a badge and a figure on a 320 px screen. §18.1
 *
 * **Both shapes read differently at a drinking table, and neither is cosmetic.**
 * A loss to everyone pays each opponent at an ordinary table and pays *nobody* at
 * a drinking one — the points are destroyed — so the ordinary line is not merely
 * vague there, it names a transfer the ledger deliberately does not write. And an
 * ordinary loss reaches the winner in full only at an ordinary table; at a
 * drinking one the winner takes the largest loss and the rest is destroyed too,
 * so "to whoever wins the hand" over-promises for every loss but one.
 *
 * `drinkingMode` is required rather than defaulted, for the reason it is required
 * on `HandDerivation`: there is no harmless default, because whichever way it
 * fell one of the two tables would be told something untrue about its own money.
 */
export function entryActionDetail(input: {
  shape: EntryShape;
  participantCount: number;
  drinkingMode: boolean;
}): string {
  const opponents = Math.max(1, input.participantCount - 1);

  if (input.shape === 'TO_EVERYONE') {
    return input.drinkingMode
      ? 'Nobody is paid. It leaves the game.'
      : `The same amount to all ${String(opponents)} others.`;
  }

  return input.drinkingMode
    ? 'Counted once. Only the largest is paid.'
    : 'Counted once, to whoever wins the hand.';
}

/**
 * The sheet a loss to everyone is committed behind. §51, §4.6
 *
 * It is the one commit worth confirming: it moves several times the number on
 * screen and it lands on the whole table at once, so the figure is spelled out
 * before it does.
 *
 * Which makes the wording load-bearing rather than decorative, and it was wrong.
 * The sheet said "Each of the other 3 players receives 20" in **both** modes,
 * while a drinking table was destroying those sixty points and crediting nobody
 * — so the one screen whose entire job is to say what is about to happen said
 * the opposite of what happened. It lives here, beside the rule it describes, so
 * that cannot drift apart again. §60
 *
 * The cost to the reporter is the same sentence in both modes — amount times
 * opponents, exactly what `handEntryImpact` prices — and only the destination
 * differs. That is the honest shape of the variant: it changes who receives, not
 * who pays.
 */
export interface PayEveryoneConfirmation {
  /** The sheet's accessible name, and its heading. */
  title: string;
  /** What it does, in one line. */
  description: string;
  /** The arithmetic under the figure, so the multiplier is never a surprise. */
  working: string;
  /** The affirmative. Named for what it does, which is not the same in both modes. */
  confirm: string;
}

export function payEveryoneConfirmation(input: {
  amount: number;
  participantCount: number;
  drinkingMode: boolean;
}): PayEveryoneConfirmation {
  const opponents = Math.max(1, input.participantCount - 1);
  const total = input.amount * opponents;
  // A table of two has one opponent, and "the other 1 players" is the sort of
  // line that makes a careful reader wonder what else was not thought through.
  const others = opponents === 1 ? '1 player' : `${String(opponents)} players`;

  if (input.drinkingMode) {
    return {
      title: 'Pay for the table?',
      description: `Nobody receives it. All ${String(total)} leaves the game.`,
      working: `${String(input.amount)} × ${others}`,
      confirm: 'Yes, pay for the table',
    };
  }

  return {
    title: 'Pay everyone?',
    description:
      opponents === 1
        ? `The other player receives ${String(input.amount)}.`
        : `Each of the other ${String(opponents)} players receives ${String(input.amount)}.`,
    working: `${String(input.amount)} × ${others}`,
    confirm: 'Yes, pay everyone',
  };
}

/** The badge on a history row. §13.2 */
export function entryShapeTag(shape: EntryShape, participantCount: number): string {
  const opponents = Math.max(1, participantCount - 1);
  return shape === 'TO_EVERYONE'
    ? `×${String(opponents)} · TO EVERYONE`
    : '−1 · SINGLE LOSS';
}

export type HandTone = 'idle' | 'waiting' | 'settled' | 'stuck';

export interface HandStatus {
  tone: HandTone;
  title: string;
  detail: string;
}

/**
 * What the hand is waiting for, in words. §3.1, §14.1
 *
 * This is the one piece of copy the new model cannot do without. A player who
 * has reported and sees no change to anyone's score needs to be told that the
 * table is still waiting on someone, not left to conclude the app dropped it —
 * and a hand where everybody has claimed a loss has to say so plainly, because
 * it is the one state no winner can be inferred from and only a human can fix.
 */
export function handStatusCopy(input: {
  reportedCount: number;
  silentCount: number;
  /** False when every entry placed itself, which TO_EVERYONE always does. §3.2 */
  needsWinner: boolean;
  winnerName: string | null;
  /**
   * Which kind of table this is. §4.6
   *
   * It changes exactly one line — the hand that needs no winner, where an
   * ordinary table pays the points out and a drinking table destroys them — and
   * it is required for the same reason it is required everywhere else: there is
   * no default that is harmless in both.
   */
  drinkingMode: boolean;
}): HandStatus {
  const { reportedCount, silentCount, needsWinner, winnerName, drinkingMode } = input;

  /**
   * The detail lines are kept to about forty characters on purpose: they sit
   * beside the draft readout on a 320 px screen, and a sentence that truncates
   * mid-word reads as a bug rather than as a hint. §18.1
   */
  if (reportedCount === 0) {
    return {
      tone: 'idle',
      title: 'Nobody has reported yet',
      detail: 'Whoever reports nothing wins it.',
    };
  }

  if (!needsWinner) {
    return {
      tone: 'settled',
      title: 'Counted',
      // Nothing was paid to the table at a drinking one: the whole cost of the
      // hand left the game and no opponent is a point better off. §4.6
      detail: drinkingMode ? 'Those points leave the game.' : 'Paid straight to the table.',
    };
  }

  if (silentCount === 0) {
    return {
      tone: 'stuck',
      title: 'Everyone reported a loss',
      detail: 'Resubmit one to settle the hand.',
    };
  }

  if (silentCount === 1) {
    return {
      tone: 'settled',
      title: `${winnerName ?? 'One player'} wins this hand`,
      detail: 'The only one who reported nothing.',
    };
  }

  return {
    tone: 'waiting',
    title: `${String(silentCount)} players still to report`,
    detail: 'The last silent player takes it.',
  };
}

/**
 * One line describing a whole hand, for history. §13.2
 *
 * A hand of ordinary losses is best named by who took it, because that is the
 * fact the table remembers. A lone loser paying everyone is named the other way
 * round, because there was no winner to speak of — at an ordinary table
 * everybody else simply received.
 *
 * At a drinking table they did not, and "lost 20 to everyone" then names a
 * distribution that never happened, directly above rows that are all zero for
 * the other players. `paid for the table` is what `AdjustmentRow` already calls
 * the ledger row underneath it, so the headline and its own detail agree. §4.6
 *
 * The mode arrives free: `handHeadline` takes a `HandDerivation`, which has
 * carried `drinkingMode` since the shape was given its own arithmetic.
 */
export function handHeadline(
  input: HandDerivation & { nameOf: (playerId: string) => string },
): string {
  const { entries, nameOf, drinkingMode } = input;
  if (entries.length === 0) return 'No losses reported';

  const { winnerPlayerId } = resolveHand(input);
  const needsWinner = entries.some((entry) => entry.shape === 'TO_WINNER');

  if (needsWinner && winnerPlayerId !== null) {
    return `${nameOf(winnerPlayerId)} won this hand`;
  }

  const only = entries.length === 1 ? entries[0] : null;
  if (only) {
    if (only.shape !== 'TO_EVERYONE') {
      return `${nameOf(only.playerId)} lost ${String(only.amount)}`;
    }
    return drinkingMode
      ? `${nameOf(only.playerId)} paid ${String(only.amount)} for the table`
      : `${nameOf(only.playerId)} lost ${String(only.amount)} to everyone`;
  }

  return `${String(entries.length)} losses reported`;
}

/**
 * What one player reported, named by what it did. §13.2, §4.6
 *
 * The same sentence was written out in two components — history's breakdown and
 * the players list — and both described the shape by what it does to *other*
 * players: "20 to all", "Reported 20 to everyone". At a drinking table it went
 * to none of them, which history states plainly two rows further down.
 *
 * One function rather than two ternaries, because a rule implemented twice is a
 * rule that will drift, and this one had already drifted from the ledger. §60
 */
export function entryOutcomeLabel(input: {
  shape: EntryShape;
  amount: number;
  drinkingMode: boolean;
}): string {
  if (input.shape !== 'TO_EVERYONE') return String(input.amount);
  return input.drinkingMode
    ? `${String(input.amount)} for the table`
    : `${String(input.amount)} to all`;
}

/**
 * Whether a completed hand is one the table celebrates, and who took it. §4.5
 *
 * The celebration marks a hand somebody *won*, which is only one of the two ways
 * a hand ends. A lone player paying everybody settles the table without anyone
 * winning it — everyone else simply received — so there is no face to put on the
 * screen and the effect stays off. That is `handOutcome`'s distinction, and this
 * reads it rather than re-deriving it, so the overlay and the ledger can never
 * come to different conclusions about what just happened. §3.2, §4.3, §60
 *
 * A voided hand never celebrates: the host deleting it takes its points back off
 * the table, and cheering for a hand that no longer moved anything would be the
 * one moment the effect actively lied. §12.5
 */
export function winnerCelebration(input: {
  round: Pick<Round, 'id' | 'participantIds' | 'voidedAt'>;
  entries: readonly HandEntry[];
  /** Participants the host has since removed, who are not waited for. §31 */
  departedPlayerIds?: readonly string[];
  /**
   * Which kind of table it was. §4.6
   *
   * Whether a hand *has* a winner does not currently depend on the mode — the
   * same reports leave the same player silent either way — so this is threaded
   * rather than needed. It is threaded anyway because the alternative is a
   * hardcoded value inside a call to `handOutcome`, which would keep working
   * right up until the day that stopped being true and would then be wrong
   * silently, on the one surface nobody tests by watching. §60
   */
  drinkingMode: boolean;
}): { roundId: string; winnerPlayerId: string } | null {
  const { round, entries, departedPlayerIds, drinkingMode } = input;
  if (round.voidedAt) return null;

  const participantIds = round.participantIds;
  if (
    handOutcome({ entries, participantIds, departedPlayerIds, drinkingMode }) !==
    'winner-inferred'
  ) {
    return null;
  }

  const winnerPlayerId = inferWinner(entries, participantIds, departedPlayerIds);
  return winnerPlayerId === null ? null : { roundId: round.id, winnerPlayerId };
}
